import type { SxProps, Theme } from "@mui/material";
import { Alert, Box, CircularProgress } from "@mui/material";
import { t } from "i18next";
import { type ReactNode, useEffect, useState } from "react";

import { useDisplayImage } from "@/hooks/useDisplayImage";

type DisplayImageProps = {
  image: { key?: string; url: string | null | undefined } | null | undefined;
  alt?: string;
  className?: string;
  sx?: SxProps<Theme>;
  onClick?: () => void;
  onError?: (error: Error) => void;
  showLoader?: boolean;
  fallback?: ReactNode;
};

export function DisplayImage({
  image,
  alt = "",
  className,
  sx,
  onClick,
  onError,
  showLoader = true,
  fallback,
}: DisplayImageProps) {
  const { imageUrl, isLoading, error } = useDisplayImage(image);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  useEffect(() => {
    if (error) onError?.(error);
  }, [error, onError]);

  if (error || (imageUrl && failedUrl === imageUrl)) {
    return fallback ?? <Alert severity="error">{t("document.review.previewError")}</Alert>;
  }

  if (isLoading && showLoader) {
    return (
      <Box
        className={className}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...sx,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!imageUrl) return fallback ?? null;

  return (
    <Box
      component="img"
      className={className}
      src={imageUrl}
      alt={alt}
      onClick={onClick}
      sx={sx}
      onError={() => {
        setFailedUrl(imageUrl);
        onError?.(new Error("Failed to load image"));
      }}
    />
  );
}
